import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, createBlock as _createBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "text-left flex flex-col sm:gap-8 gap-4" }
const _hoisted_4 = { class: "transition-all flex flex-col sm:gap-8 gap-4" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 2 }
const _hoisted_7 = {
  key: 3,
  class: "flex flex-col sm:gap-8 gap-4"
}
const _hoisted_8 = { class: "flex items-center gap-2" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("span", { class: "sr-only" }, "Use settings", -1)
const _hoisted_10 = { class: "flex justify-between w-full" }
const _hoisted_11 = ["disabled"]

import { Switch } from "@headlessui/vue";
import { ArrowLongRightIcon } from "@heroicons/vue/24/solid";
import { Form, FormContext, SubmissionHandler } from "vee-validate";
import { computed, PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import Modal from "shared/components/modals/Modal.vue";
import { NonWorkingDay } from "shared/types/ProjectDurationSettings";
import DateField from "@/components/forms/DateField.vue";
import Field from "@/components/forms/Field.vue";
import SelectListField from "@/components/forms/SelectListField.vue";
import { disturbances } from "@/constants/disturbances";
import { nonWorkingDaysTypes } from "@/constants/workingDays";
import DateRangeField from "@/views/daily_report/components/DateRangeField.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'NonWorkingDaysEditor',
  props: {
  nonWorkingDay: {
    type: Object as PropType<NonWorkingDay>,
    required: false,
    default: () => ({}), // Default to an empty object
  },
},
  emits: ["commit", "close"],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();
const hasSubmitted = ref(false);

const props = __props;

const schema = computed(() =>
  yup.object({
    name: yup.string().required(t("admin.durations_project_settings.validation_name_empty")),
    date: yup
      .date()
      .nullable()
      .when("type", {
        is: "public_holiday",
        then: (schema) =>
          schema.required(t("admin.durations_project_settings.validation_date_required")),
        otherwise: (schema) => schema.notRequired(),
      }),
    range: yup
      .array()
      .of(yup.date().nullable())
      .when("type", {
        is: (val: string) => val !== "public_holiday",
        then: (schema) =>
          schema
            .required(t("admin.durations_project_settings.validation_date_required"))
            .test(
              "is-valid-range",
              t("admin.durations_project_settings.validation_date_required"),
              (value: (Date | null | undefined)[]) => {
                // Check that range is not empty and has exactly two dates
                return value.length === 2 && value.every((date) => date != null);
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    type: yup.string().required(),
    disturbance_type: yup
      .string()
      .required()
      .when("type", {
        is: (val: string) => val === "disturbance",
        then: (schema) =>
          schema.required(t("admin.durations_project_settings.validation_disturbance_type_empty")),
        otherwise: (schema) => schema.notRequired(),
      }),
    is_critical: yup.boolean().required(),
    disturbance_description: yup.string().nullable().default(null),
  }),
);

const initialValues = computed(() => {
  if (props.nonWorkingDay && props?.nonWorkingDay?.type) {
    return {
      ...props.nonWorkingDay,
      type: props.nonWorkingDay.type,
      date: props.nonWorkingDay?.type === "public_holiday" ? props.nonWorkingDay?.start_date : null,
      range:
        props.nonWorkingDay?.type === "public_holiday"
          ? [props.nonWorkingDay?.start_date, props.nonWorkingDay?.start_date]
          : [props.nonWorkingDay?.start_date, props.nonWorkingDay?.end_date],
      disturbance_type:
        props.nonWorkingDay?.type === "disturbance" ? props.nonWorkingDay?.disturbance_type : null,
    };
  }
  return { type: "", is_critical: false, disturbance_type: "" };
});

const formRef = ref<FormContext | null>(null);
const showTypeField = ref(!props.nonWorkingDay);

const emit = __emit;

const handleSubmit: SubmissionHandler = (values) => {
  const { type, range, date, is_critical, disturbance_type, disturbance_description, ...rest } =
    values;
  const holiday = {
    ...rest,
    is_critical: type !== "disturbance" ? true : is_critical,
    type: type,
    start_date: type === "public_holiday" ? date : range[0],
    end_date: type === "public_holiday" ? date : range[1],
    disturbance_type: disturbance_type || null,
    disturbance_description: disturbance_description || null,
  };
  emit("commit", holiday);
};
const typeOptions = computed(() => {
  return nonWorkingDaysTypes.map((item) => ({
    value: item,
    name: t(`admin.durations_project_settings.non_working_day_type_labels.${item}`),
  }));
});

const disturbanceOptions = computed(() =>
  disturbances.map((disturbance) => ({
    name: t(`disturbances.type.${disturbance}`) || disturbance,
    value: disturbance,
  })),
);

const toggleTypeSelection = () => {
  showTypeField.value = !showTypeField.value;
};

watch(
  () => formRef.value?.values?.type,
  (newValue) => {
    if (!newValue) {
      hasSubmitted.value = false;
      showTypeField.value = true;
    }
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    open: true,
    onClose: _cache[2] || (_cache[2] = ($event: any) => (emit('close'))),
    customCls: "w-full sm:w-max lg:w-1/4"
  }, {
    title: _withCtx(() => [
      (props.nonWorkingDay && props.nonWorkingDay.type)
        ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(_unref(t)("buttons.edit")) + " " + _toDisplayString(_ctx.$t(
            `admin.durations_project_settings.non_working_day_type_labels.${props.nonWorkingDay.type}`,
          )), 1))
        : (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(showTypeField.value || !formRef.value?.values?.type
            ? _ctx.$t("admin.durations_project_settings.holidays_editor_header")
            : `${_ctx.$t("admin.durations_project_settings.add")} ${_ctx.$t(
                `admin.durations_project_settings.non_working_day_type_labels.${formRef.value?.values?.type}`,
              )}`), 1))
    ]),
    content: _withCtx(() => [
      _createVNode(_unref(Form), {
        onSubmit: handleSubmit,
        onInvalidSubmit: _cache[1] || (_cache[1] = ($event: any) => (hasSubmitted.value = true)),
        initialValues: initialValues.value,
        validationSchema: schema.value,
        ref_key: "formRef",
        ref: formRef
      }, {
        default: _withCtx(({ values, setFieldValue }) => [
          _createElementVNode("div", _hoisted_3, [
            _withDirectives(_createVNode(SelectListField, {
              name: "type",
              label: _ctx.$t('admin.durations_project_settings.holidays_field_type'),
              options: typeOptions.value,
              minWidth: 250,
              placeholder: `-- ${_ctx.$t('form.select_option')} --`,
              onUpdate: _cache[0] || (_cache[0] = ($event: any) => (showTypeField.value = false))
            }, null, 8, ["label", "options", "placeholder"]), [
              [_vShow, showTypeField.value]
            ]),
            _withDirectives(_createElementVNode("div", _hoisted_4, [
              _createVNode(Field, {
                name: "name",
                label: _unref(t)('admin.durations_project_settings.holidays_field_name')
              }, null, 8, ["label"]),
              (values?.type === 'disturbance')
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createVNode(SelectListField, {
                      name: "disturbance_type",
                      label: _unref(t)('admin.durations_project_settings.holidays_field_disturbance_type'),
                      options: disturbanceOptions.value,
                      minWidth: 250,
                      placeholder: `-- ${_ctx.$t('form.select_option')} --`
                    }, null, 8, ["label", "options", "placeholder"])
                  ]))
                : _createCommentVNode("", true),
              (values.type === 'public_holiday')
                ? (_openBlock(), _createBlock(DateField, {
                    key: 1,
                    name: "date",
                    label: _unref(t)('admin.durations_project_settings.holidays_field_date')
                  }, null, 8, ["label"]))
                : _createCommentVNode("", true),
              (values.type && ['disturbance', 'company_holiday'].includes(values.type))
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createVNode(DateRangeField, {
                      name: "range",
                      modelValue: values.range,
                      "onUpdate:modelValue": ($event: any) => ((values.range) = $event),
                      "enable-time-picker": false,
                      label: _unref(t)('admin.durations_project_settings.holidays_field_date'),
                      "auto-apply": "",
                      range: ""
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "label"])
                  ]))
                : _createCommentVNode("", true),
              (values.type === 'disturbance')
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _createVNode(Field, {
                      as: "textarea",
                      name: "disturbance_description",
                      inputClass: "max-h-24",
                      label: 
                  _unref(t)('admin.durations_project_settings.holidays_field_disturbance_description')
                
                    }, null, 8, ["label"]),
                    _createElementVNode("label", _hoisted_8, [
                      _createTextVNode(_toDisplayString(_unref(t)("working_day.inactive_days")) + "? ", 1),
                      _createVNode(_unref(Switch), {
                        modelValue: values.is_critical,
                        "onUpdate:modelValue": ($event: any) => (setFieldValue('is_critical', $event)),
                        class: "relative inline-flex h-5 w-10 cursor-pointer rounded-full"
                      }, {
                        default: _withCtx(() => [
                          _hoisted_9,
                          _createElementVNode("span", {
                            class: _normalizeClass([
                      values.is_critical ? 'bg-yellow-500' : 'bg-gray-200',
                      'absolute h-5 w-9 rounded-full transition-colors',
                    ])
                          }, null, 2),
                          _createElementVNode("span", {
                            class: _normalizeClass([
                      values.is_critical ? 'translate-x-5' : 'translate-x-0',
                      'absolute left-0 h-5 w-5 transform rounded-full bg-white transition-transform',
                    ])
                          }, null, 2)
                        ]),
                        _: 2
                      }, 1032, ["modelValue", "onUpdate:modelValue"])
                    ])
                  ]))
                : _createCommentVNode("", true)
            ], 512), [
              [_vShow, !showTypeField.value && values?.type]
            ]),
            _createElementVNode("template", _hoisted_10, [
              _withDirectives(_createElementVNode("button", {
                disabled: !values.type,
                type: "button",
                onClick: toggleTypeSelection,
                class: _normalizeClass([
                'inline-flex items-center md:px-4 md:py-1.5 py-1 px-1.5 rounded-md font-bold border shadow transition-colors duration-150',
                showTypeField.value ? 'ml-auto' : '',
                values.type
                  ? 'text-yellow border-yellow hover:bg-yellow hover:text-white bg-gray-50'
                  : 'border-gray-100 text-gray-300 bg-gray-100 cursor-not-allowed', // Gray when disabled
              ])
              }, [
                _createVNode(_unref(ArrowLongRightIcon), {
                  class: _normalizeClass([showTypeField.value ? 'rotate-0' : 'rotate-180', "shrink-0 h-6 transition-transform duration-100"])
                }, null, 8, ["class"])
              ], 10, _hoisted_11), [
                [_vShow, !props.nonWorkingDay.type]
              ]),
              _withDirectives(_createElementVNode("button", {
                type: "submit",
                class: _normalizeClass([
                'focus:outline-none w-max rounded-md bg-yellow-500 px-4 py-2 text-sm text-white shadow-sm hover:bg-yellow-600 justify-self-end',
                !showTypeField.value && 'ml-auto',
              ])
              }, _toDisplayString(_unref(t)("buttons.apply")), 3), [
                [_vShow, values.type && !showTypeField.value]
              ])
            ])
          ])
        ]),
        _: 1
      }, 8, ["initialValues", "validationSchema"])
    ]),
    _: 1
  }))
}
}

})